import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Range, RangeKeyDict } from 'react-date-range';

import useOnClickOutside from '@utils/hooks';

import {
  buildCustomFields,
  deriveSelectAllState,
  getFieldSelectedCount,
  getFirstEnabledField,
  getInitialDateRange,
  isFieldDisabled
} from './multiFieldFilterHelpers';
import {
  MultiFieldFilterV2Props,
  SelectAllModes,
  SelectedFilterValues
} from './types';

const useMultiFieldFilter = ({
  fieldOptions,
  onApply,
  selectedFilters,
  type = 'multi',
  customFields,
  setCustomFields
}: Omit<MultiFieldFilterV2Props, 'buttonClass'>) => {
  const initialSelected = useMemo(
    () => (Object.keys(selectedFilters).length ? selectedFilters : {}),
    [selectedFilters]
  );

  const derivedState = useMemo(
    () => deriveSelectAllState(fieldOptions),
    [fieldOptions]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [activeField, setActiveField] = useState(() =>
    getFirstEnabledField(fieldOptions, initialSelected)
  );
  const [selected, setSelected] =
    useState<SelectedFilterValues>(initialSelected);
  const [selectAllModes, setSelectAllModes] = useState<SelectAllModes>(
    () => derivedState.modes
  );
  const [excludedItems, setExcludedItems] = useState<SelectedFilterValues>(
    () => derivedState.excluded
  );
  const [dateRange, setDateRange] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  });
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const optionsScrollRef = useRef<HTMLDivElement>(null);

  const currentField = fieldOptions[activeField];
  const isSingleSelect = currentField?.singleSelect || type === 'single';
  const hasOptions = (currentField?.options?.length ?? 0) > 0;

  const totalSelected = useMemo(
    () =>
      Object.values(fieldOptions).reduce(
        (total, field) =>
          total +
          getFieldSelectedCount(
            field,
            selectedFilters,
            derivedState.modes,
            derivedState.excluded
          ),
        0
      ),
    [selectedFilters, derivedState, fieldOptions]
  );

  const isSelectAllMode = selectAllModes[activeField] || false;

  const isAllSelected = useMemo(() => {
    if (isSelectAllMode) {
      const excluded = excludedItems[activeField] || [];
      const hasActiveSearch = !!searchTerms[activeField];
      if (hasActiveSearch && hasOptions) {
        return (
          currentField?.options?.every((o) => !excluded.includes(o.value)) ??
          false
        );
      }
      return excluded.length === 0;
    }
    const hasActiveSearch = !!searchTerms[activeField];
    if (hasActiveSearch && hasOptions) {
      const current = selected[activeField] || [];
      const allVisible = currentField?.options?.every((o) =>
        current.includes(o.value)
      );
      return allVisible ?? false;
    }
    if (currentField?.hasPagination) {
      const count = selected[activeField]?.length || 0;
      return count === (currentField?.totalCount || 0);
    }
    return (
      hasOptions &&
      selected[activeField]?.length === currentField?.options?.length
    );
  }, [
    currentField,
    selected,
    activeField,
    isSelectAllMode,
    hasOptions,
    excludedItems,
    searchTerms
  ]);

  useEffect(() => {
    if (currentField && isFieldDisabled(currentField, selected)) {
      setActiveField(getFirstEnabledField(fieldOptions, selected));
    }
  }, [selected, fieldOptions, currentField]);

  useEffect(() => {
    const el = optionsScrollRef.current;
    if (!el || !currentField?.hasPagination) return;
    if (currentField.isLoading || !currentField.hasMore) return;
    if (!currentField.options?.length) return;
    if (el.scrollHeight <= el.clientHeight) currentField.onLoadMore?.();
  }, [currentField]);

  const handleOptionsScroll = useCallback(() => {
    const el = optionsScrollRef.current;
    if (!el || !currentField?.hasPagination) return;
    if (currentField.isLoading || !currentField.hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 50)
      currentField.onLoadMore?.();
  }, [currentField]);

  const close = useCallback(() => {
    Object.values(fieldOptions).forEach((f) => {
      if (f.hasSearch && searchTerms[f.field]) f.onSearch?.('');
    });
    setIsOpen(false);
    setActiveField(getFirstEnabledField(fieldOptions, initialSelected));
    setSelected(initialSelected);
    setSelectAllModes(derivedState.modes);
    setExcludedItems(derivedState.excluded);
    setSearchTerms({});
  }, [fieldOptions, initialSelected, derivedState, searchTerms]);

  const open = useCallback(() => {
    const initial = getFirstEnabledField(fieldOptions, initialSelected);
    setIsOpen(true);
    setSelected(initialSelected);
    setSelectAllModes(derivedState.modes);
    setExcludedItems(derivedState.excluded);
    setSearchTerms({});
    setActiveField(initial);
    setDateRange(getInitialDateRange(customFields, initial));
    fieldOptions[initial]?.onSelect?.();
  }, [fieldOptions, initialSelected, derivedState, customFields]);

  useOnClickOutside(containerRef, (e) => {
    e.stopPropagation();
    close();
  });

  const selectField = useCallback(
    (field: string) => {
      const prevField = fieldOptions[activeField];
      if (prevField?.hasSearch && searchTerms[activeField]) {
        setSearchTerms((prev) => ({ ...prev, [activeField]: '' }));
        prevField.onSearch?.('');
      }
      setActiveField(field);
      setDateRange(getInitialDateRange(customFields, field));
      fieldOptions[field]?.onSelect?.();
    },
    [customFields, fieldOptions, activeField, searchTerms]
  );

  const toggleItem = useCallback(
    (item: { value: string }, checked: boolean) => {
      if (isSingleSelect) {
        setSelected((prev) => ({
          ...prev,
          [activeField]: [item.value]
        }));
        return;
      }
      if (selectAllModes[activeField]) {
        setExcludedItems((prev) => {
          const current = prev[activeField] || [];
          if (checked)
            return {
              ...prev,
              [activeField]: current.filter((v) => v !== item.value)
            };
          return { ...prev, [activeField]: [...current, item.value] };
        });
      } else {
        setSelected((prev) => {
          const current = prev[activeField] || [];
          return {
            ...prev,
            [activeField]: checked
              ? [...current, item.value]
              : current.filter((v) => v !== item.value)
          };
        });
      }
    },
    [activeField, isSingleSelect, selectAllModes]
  );

  const toggleSelectAllExcludeMode = useCallback(
    (checked: boolean) => {
      setSelectAllModes((prev) => ({ ...prev, [activeField]: checked }));
      setExcludedItems((prev) => ({ ...prev, [activeField]: [] }));
      if (!checked) setSelected((prev) => ({ ...prev, [activeField]: [] }));
    },
    [activeField]
  );

  const toggleSelectAllIncludeMode = useCallback(
    (checked: boolean) => {
      const all = currentField?.options?.map((o) => o.value) || [];
      setSelected((prev) => {
        const current = prev[activeField] || [];
        if (!checked) {
          return {
            ...prev,
            [activeField]: current.filter((v) => !all.includes(v))
          };
        }
        const merged = [...new Set([...current, ...all])];
        return { ...prev, [activeField]: merged };
      });
      if (currentField?.hasSelectAll) {
        setSelectAllModes((prev) => ({
          ...prev,
          [activeField]: false
        }));
        setExcludedItems((prev) => ({ ...prev, [activeField]: [] }));
      }
    },
    [activeField, currentField]
  );

  const toggleSelectAllSearchInExcludeMode = useCallback(
    (checked: boolean) => {
      const visibleValues = currentField?.options?.map((o) => o.value) || [];
      setExcludedItems((prev) => {
        const current = prev[activeField] || [];
        if (checked) {
          return {
            ...prev,
            [activeField]: current.filter((v) => !visibleValues.includes(v))
          };
        }
        const merged = [...new Set([...current, ...visibleValues])];
        return { ...prev, [activeField]: merged };
      });
    },
    [activeField, currentField]
  );

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      const hasActiveSearch = !!searchTerms[activeField];
      const isInExcludeMode =
        currentField?.hasSelectAll && currentField?.hasPagination;

      if (isInExcludeMode && !hasActiveSearch) {
        toggleSelectAllExcludeMode(checked);
      } else if (isInExcludeMode && hasActiveSearch && isSelectAllMode) {
        toggleSelectAllSearchInExcludeMode(checked);
      } else {
        toggleSelectAllIncludeMode(checked);
      }
    },
    [
      activeField,
      currentField,
      searchTerms,
      isSelectAllMode,
      toggleSelectAllExcludeMode,
      toggleSelectAllIncludeMode,
      toggleSelectAllSearchInExcludeMode
    ]
  );

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerms((prev) => ({ ...prev, [activeField]: term }));
      currentField?.onSearch?.(term);
    },
    [activeField, currentField]
  );

  const handleDateChange = useCallback((obj: RangeKeyDict) => {
    setDateRange(obj.selection);
  }, []);

  const setFieldSelected = useCallback((field: string, values: string[]) => {
    setSelected((prev) => ({ ...prev, [field]: values }));
  }, []);

  const handleClear = useCallback(() => {
    onApply({}, undefined, {}, {});
    setDateRange({
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    });
    setCustomFields?.([]);
    close();
  }, [onApply, setCustomFields, close]);

  const handleApply = useCallback(() => {
    let updatedCustomFields = customFields;
    if (setCustomFields) {
      updatedCustomFields = buildCustomFields(
        customFields,
        activeField,
        dateRange
      );
      setCustomFields(updatedCustomFields);
    }
    onApply(selected, updatedCustomFields, selectAllModes, excludedItems);
    close();
  }, [
    customFields,
    setCustomFields,
    activeField,
    dateRange,
    onApply,
    selected,
    selectAllModes,
    excludedItems,
    close
  ]);

  return {
    isOpen,
    open,
    close,
    activeField,
    selectField,
    selected,
    selectAllModes,
    excludedItems,
    isSelectAllMode,
    isAllSelected,
    totalSelected,
    currentField,
    isSingleSelect,
    hasOptions,
    searchTerms,
    handleSearch,
    dateRange,
    handleDateChange,
    toggleItem,
    toggleSelectAll,
    setFieldSelected,
    handleOptionsScroll,
    handleApply,
    handleClear,
    containerRef,
    optionsScrollRef
  };
};

export default useMultiFieldFilter;
